import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface PaletteColor {
    main: string;
    body: string;
    subtitle: string;
    aqua: string;
    purple: string;
    purpleDark: string;
    breadcrumbsLink: string;
    tableBorder: string;
    tableHeadBg: string;
    vocabularyUnlearned: string;
    vocabularyLearned: string;
    vocabularyActiveSpeech: string;
  }
  // Mirror the custom PaletteColor fields onto the *options* type too, so the
  // literals passed to `createTheme({ palette: { primary: { body, ... } } })`
  // type-check. TS 4.9 skipped excess-property checks on the PaletteColorOptions
  // union; TS 5 enforces them, so augmenting only PaletteColor above is not
  // enough — the input side needs these (optional) fields as well.
  interface SimplePaletteColorOptions {
    body?: string;
    subtitle?: string;
    aqua?: string;
    purple?: string;
    purpleDark?: string;
    breadcrumbsLink?: string;
    tableBorder?: string;
    tableHeadBg?: string;
    vocabularyUnlearned?: string;
    vocabularyLearned?: string;
    vocabularyActiveSpeech?: string;
  }
  // interface TypographyVariants {
  //   subtitle: React.CSSProperties;
  // }
  // interface TypographyVariantsOptions {
  //   subtitle: React.CSSProperties;
  // }
}

declare module '@mui/material/Typography' {
  // interface TypographyPropsVariantOverrides {
  //   subtitle: true;
  // }
}

/**
 * Min viewport width (px) at which a page may switch to a "wide" layout that
 * places a fixed-width sidebar/rail next to a *generous multi-column* content
 * area AND competes for the same horizontal space with other wide chrome
 * (e.g. the vocabulary test's active screen: 236px rail + up to 4 word
 * columns + a 3-slot centered status bar all at once).
 *
 * Deliberately NOT one of the theme's named breakpoints: `md` (900) is too
 * low for that kind of triple-squeeze — an iPad Pro in portrait (1024) and
 * narrower laptops don't have the room and the layout overlaps/wraps; `lg`
 * (1580) is too high — ordinary laptop widths (1280/1366) would get stuck in
 * the compact layout. 1200 sits between them. Use via
 * `theme.breakpoints.up(DESKTOP_LAYOUT_BREAKPOINT)` / `useMediaQuery(...)` /
 * `theme.breakpoints.between('sm', DESKTOP_LAYOUT_BREAKPOINT)`.
 *
 * Only opt a page into this if it genuinely has that triple-squeeze. Pages
 * with a lighter sidebar + reflowable content (WordSet, the irregular-verb
 * browse modes, Community — all via CategoryRail, which already swaps to a
 * mobile chip strip below `md`) are fine on `md` and should NOT be moved here
 * without first confirming they actually break in the 900–1200 range.
 *
 * Kept a plain constant rather than a custom named breakpoint on purpose:
 * MUI's static `breakpointKeys` is hardcoded to xs/sm/md/lg/xl, so a custom
 * key can't be used safely in the `{ xs, desktop }` responsive-object
 * shorthand anyway — and a shared constant needs no module augmentation and
 * doesn't mutate the breakpoint scale.
 */
export const DESKTOP_LAYOUT_BREAKPOINT = 1200;

export const theme = createTheme({
  colorSchemes: {
    light: {
      palette: {
        primary: {
          main: '#3B2C93', // main button color
          body: '#f5f6f8', // document background
          subtitle: '#7A7F88', // subtitle text,
          aqua: '#27CCBC',
          purple: '#BFB4FF',
          purpleDark: '#777BFF',
          breadcrumbsLink: '#488BFF',
          tableBorder: '#BBBFC7',
          tableHeadBg: '#DDE1E6',
          vocabularyUnlearned: '#E7E7E7',
          vocabularyLearned: '#D1E1BA',
          vocabularyActiveSpeech: '#B3D6B8',
        },
        secondary: {
          main: '#E2330A',
          body: '#F7F3F3',
        },
        text: {
          primary: '#141414', // default text color
          // Phase B: surface secondary text on the standard MUI slot so consumers
          // can write `color="text.secondary"` instead of `color="primary.subtitle"`.
          // Same value as primary.subtitle for backward compat — that legacy alias
          // stays in palette until callers are migrated in Phase F.
          secondary: '#7A7F88',
        },
        // Phase B: explicit background slots. Previously primary.body (#f5f6f8) was
        // applied as the page bg via the MuiCssBaseline override; this exposes it on
        // the standard MUI slot so any component can do `bgcolor: 'background.default'`.
        background: {
          default: '#f5f6f8',
          paper: '#ffffff',
        },
      },
    },
    dark: {
      palette: {
        primary: {
          main: '#9B7CFF',
          body: '#121722',
          subtitle: '#AAB4C2',
          aqua: '#173C3A',
          purple: '#33275A',
          purpleDark: '#33275A',
          breadcrumbsLink: '#8DB2FF',
          tableBorder: '#303A48',
          tableHeadBg: '#2F3845',
          vocabularyUnlearned: '#303A48',
          vocabularyLearned: '#505f4a',
          vocabularyActiveSpeech: '#2E4D35',
        },
        secondary: {
          main: '#F87171',
          body: '#1D2430',
        },
        background: {
          default: '#121722',
          paper: '#1D2430',
        },
        text: {
          primary: '#F3F6FA',
          secondary: '#AAB4C2',
        },
      },
    },
  },
  breakpoints: {
    values: {
      lg: 1580,
      md: 900,
      sm: 600,
      xl: 1730,
      xs: 0,
    },
  },
  typography: {
    subtitle1: {
      fontSize: 20,
      marginTop: 10,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: 20,
      lineHeight: 1.5,
    },
    h1: {
      fontSize: 30,
      fontWeight: 700,
    },
    h3: {
      fontSize: 25,
      fontWeight: 600,
    },
    h4: {
      fontSize: 21,
      fontWeight: 600,
    },
    h5: {
      fontSize: 19,
      fontWeight: 600,
    },
  },
  spacing: 8,
  components: {
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        body: {
          backgroundColor: theme.palette.primary.body,
          color: theme.palette.text.primary,
        },
        a: {
          color: 'inherit',
          textDecoration: 'none',
        },
      }),
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          boxShadow: 'none',
          borderRadius: 16,
          padding: '20px',
          backgroundImage: 'none',
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
      },
      styleOverrides: {
        // Phase C: shared root — size + shape only. Per-variant styling lives in
        // the variants array so contained / outlined / text each render distinctly.
        root: () => ({
          minHeight: 36,
          borderRadius: 8,
          boxShadow: 'none',
          paddingTop: 8,
          paddingBottom: 8,
          paddingLeft: 18,
          paddingRight: 18,
          textTransform: 'none',
          fontSize: 14,
          fontWeight: 400,
          lineHeight: 1.4,
          letterSpacing: 0,
          // Match the 6px gap used in design mockups instead of MUI's default 8px.
          '& .MuiButton-startIcon': {
            marginRight: 6,
            marginLeft: -2,
          },
          '& .MuiButton-endIcon': {
            marginLeft: 6,
            marginRight: -2,
          },
          '&:hover': {
            boxShadow: 'none',
          },
        }),
      },
      variants: [
        {
          props: { variant: 'contained' },
          style: ({ theme }) => ({
            // Per design preference: drop the solid primary fill, render as a
            // bordered button (visually identical to `outlined`). Hierarchy via
            // surrounding context, not heavy color blocks.
            backgroundColor: 'transparent',
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            '& .MuiButton-startIcon': {
              color: 'inherit',
            },
            '& svg': {
              color: 'inherit',
            },
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'light'
                  ? 'rgba(59, 44, 147, 0.06)'
                  : 'rgba(155, 124, 255, 0.1)',
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
            },
            '&.Mui-disabled': {
              backgroundColor: 'transparent',
              borderColor:
                theme.palette.mode === 'light'
                  ? theme.palette.primary.tableBorder
                  : '#303A48',
              color:
                theme.palette.mode === 'light'
                  ? theme.palette.text.secondary
                  : '#AAB4C2',
              opacity: theme.palette.mode === 'dark' ? 1 : undefined,
            },
          }),
        },
        {
          props: { variant: 'outlined' },
          style: ({ theme }) => ({
            // Phase C fix: was rendering as primary fill + white text (identical to
            // contained). Now properly transparent with primary border + primary text.
            backgroundColor: 'transparent',
            borderWidth: 1,
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            '& .MuiButton-startIcon': {
              color: 'inherit',
            },
            '& svg': {
              color: 'inherit',
            },
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'light'
                  ? 'rgba(59, 44, 147, 0.04)'
                  : 'rgba(155, 124, 255, 0.08)',
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
            },
            '&.Mui-disabled': {
              backgroundColor: 'transparent',
              borderColor:
                theme.palette.mode === 'light'
                  ? theme.palette.primary.tableBorder
                  : '#303A48',
              color:
                theme.palette.mode === 'light'
                  ? theme.palette.text.secondary
                  : '#AAB4C2',
              opacity: theme.palette.mode === 'dark' ? 1 : undefined,
            },
          }),
        },
        {
          props: { variant: 'text' },
          style: ({ theme }) => ({
            backgroundColor: 'transparent',
            color: theme.palette.primary.main,
            '& .MuiButton-startIcon': {
              color: 'inherit',
            },
            '& svg': {
              color: 'inherit',
            },
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'light'
                  ? 'rgba(59, 44, 147, 0.04)'
                  : 'rgba(155, 124, 255, 0.08)',
              color: theme.palette.primary.main,
            },
            '&.Mui-disabled': {
              backgroundColor: 'transparent',
              color:
                theme.palette.mode === 'light'
                  ? theme.palette.text.secondary
                  : '#AAB4C2',
              opacity: theme.palette.mode === 'dark' ? 1 : undefined,
            },
          }),
        },
      ],
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          backgroundImage: 'none',
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
    },
  },
});
